import { useState, useEffect, useMemo } from "react";
import { Star, Pencil, Trash2, ExternalLink, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import Sparkline from "@/components/Sparkline";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

function initials(name) {
    if (!name) return "?";
    return name
        .split(/\s+/)
        .map((w) => w[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();
}

function relativeTime(iso) {
    if (!iso) return "Never";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.max(0, Math.floor(diff))}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
}

const HUE_PALETTE = [210, 25, 280, 160, 340, 45, 195, 110];
function hueFor(name) {
    let h = 0;
    for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
    return HUE_PALETTE[Math.abs(h) % HUE_PALETTE.length];
}

export default function AppTile({
    app,
    status,
    onEdit,
    onDelete,
    onToggleFavorite,
    isAdmin,
    category,
    history,
}) {
    const [iconError, setIconError] = useState(false);
    useEffect(() => {
        setIconError(false);
    }, [app.icon_url]);

    const live = status || {
        status: app.last_status,
        last_checked: app.last_checked,
        response_time_ms: app.response_time_ms,
    };
    const isOffline = live?.status === "offline";

    const hue = useMemo(() => hueFor(app.name), [app.name]);

    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div
                        className="relative app-tile-hover glass-tile rounded-3xl overflow-hidden group"
                        data-testid={`app-tile-${app.id}`}
                    >
                        {isOffline && <div className="offline-edge" data-testid={`offline-edge-${app.id}`} />}

                        <a
                            href={app.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block p-5 sm:p-6"
                            data-testid={`app-link-${app.id}`}
                        >
                            <div className="flex items-start justify-between mb-5">
                                <div
                                    className="h-14 w-14 rounded-2xl flex items-center justify-center font-display font-bold text-xl shadow-sm overflow-hidden"
                                    style={{
                                        background: iconError || !app.icon_url
                                            ? `linear-gradient(135deg, hsl(${hue} 70% 55%), hsl(${(hue + 40) % 360} 70% 45%))`
                                            : "rgba(255,255,255,0.5)",
                                        color: "white",
                                    }}
                                >
                                    {!iconError && app.icon_url ? (
                                        <img
                                            src={app.icon_url}
                                            alt={app.name}
                                            className="h-full w-full object-cover"
                                            onError={() => setIconError(true)}
                                        />
                                    ) : (
                                        <span>{initials(app.name)}</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-1.5">
                                    {category && (
                                        <span
                                            className="text-[11px] font-medium px-2 py-1 rounded-full flex items-center gap-1 bg-foreground/5 text-muted-foreground"
                                            style={category.color ? { color: category.color } : undefined}
                                            title={category.name}
                                            data-testid={`app-category-${app.id}`}
                                        >
                                            <span>{category.icon}</span>
                                            <span className="hidden sm:inline max-w-[6rem] truncate">
                                                {category.name}
                                            </span>
                                        </span>
                                    )}
                                    {app.favorite && (
                                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                    )}
                                    <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                            </div>
                            <h3 className="font-display font-semibold text-lg leading-tight tracking-tight truncate">
                                {app.name}
                            </h3>
                            <p className="text-xs text-muted-foreground mt-1 truncate font-mono">
                                {app.host}:{app.port}
                            </p>
                            {app.description && (
                                <p className="text-sm text-muted-foreground mt-3 line-clamp-2 leading-relaxed">
                                    {app.description}
                                </p>
                            )}
                            <div className="mt-4 flex items-center gap-3 text-xs">
                                <div className="flex items-center gap-1.5">
                                    <span
                                        className={`h-2 w-2 rounded-full ${
                                            live?.status === "online"
                                                ? "bg-emerald-500"
                                                : isOffline
                                                  ? "bg-red-500"
                                                  : "bg-muted-foreground/40"
                                        }`}
                                    />
                                    <span className="text-muted-foreground capitalize">
                                        {live?.status || "unknown"}
                                    </span>
                                </div>
                                {live?.response_time_ms != null && (
                                    <div className="flex items-center gap-1 text-muted-foreground">
                                        <Activity className="h-3 w-3" />
                                        {live.response_time_ms}ms
                                    </div>
                                )}
                                <div className="text-muted-foreground ml-auto">
                                    {relativeTime(live?.last_checked)}
                                </div>
                            </div>
                            {history?.points?.length > 0 && (
                                <div
                                    className="mt-3 pt-3 border-t border-border/50 flex items-end justify-between gap-2"
                                    data-testid={`app-uptime-${app.id}`}
                                >
                                    <Sparkline points={history.points} />
                                    <div className="text-right leading-tight">
                                        <span className="text-xs font-semibold tabular-nums">
                                            {history.uptime_pct != null
                                                ? `${history.uptime_pct}%`
                                                : "—"}
                                        </span>
                                        <span className="block text-[10px] text-muted-foreground">
                                            24h uptime
                                        </span>
                                    </div>
                                </div>
                            )}
                        </a>

                        {isAdmin && (
                            <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                <Button
                                    size="icon"
                                    variant="secondary"
                                    className="h-8 w-8 rounded-full"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onToggleFavorite?.(app);
                                    }}
                                    data-testid={`favorite-${app.id}`}
                                    aria-label="favorite"
                                >
                                    <Star
                                        className={`h-3.5 w-3.5 ${app.favorite ? "fill-yellow-400 text-yellow-400" : ""}`}
                                    />
                                </Button>
                                <Button
                                    size="icon"
                                    variant="secondary"
                                    className="h-8 w-8 rounded-full"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onEdit?.(app);
                                    }}
                                    data-testid={`edit-app-${app.id}`}
                                    aria-label="edit"
                                >
                                    <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    size="icon"
                                    variant="secondary"
                                    className="h-8 w-8 rounded-full text-red-500 hover:text-red-500"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onDelete?.(app);
                                    }}
                                    data-testid={`delete-app-${app.id}`}
                                    aria-label="delete"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        )}
                    </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                    <div className="space-y-1">
                        <p className="font-semibold">{app.name}</p>
                        {app.description && <p className="text-xs">{app.description}</p>}
                        <p className="text-xs opacity-80">
                            {app.url}
                        </p>
                        {live?.last_checked && (
                            <p className="text-xs opacity-80">
                                Last check: {relativeTime(live.last_checked)} · {live.response_time_ms ?? "—"}ms
                            </p>
                        )}
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
